import { useCallback, useEffect, useRef } from 'react'

/**
 * Der Rückweg aus dem **Geschafft-Schalter**: Wer dort den Haken herausnimmt,
 * soll die Aufgabe wiederfinden — ausgewählt und im Bild.
 *
 * Auswählen lässt sie sich aber erst, wenn sie in der Liste steht, und dorthin
 * kommt sie ein Bild später (optimistische Überschreibung) bis eine Sekunde
 * später (Dexie → LiveQuery). Deshalb wird die Kennung vorgemerkt und erst
 * ausgewählt, sobald `visibleIds` sie zeigt — dasselbe Muster, mit dem
 * `useDuplicateItems` die neue Zeile findet.
 *
 * Das Scrollen macht danach `TaskEntry` selbst: Sein Fokus-Effekt holt die
 * Zeile ins Bild, wenn sie außerhalb liegt.
 *
 * @example
 * const markPending = usePendingRestore(flatNavList.map(i => i.id), selectTask)
 * const restore = (id: string) => { markPending(id); handleToggleComplete(new Set([id])) }
 */
export function usePendingRestore(
  visibleIds: readonly string[],
  select: (id: string) => void,
): (id: string) => void {
  const pendingRef = useRef<string | null>(null)
  const selectRef = useRef(select)
  selectRef.current = select

  useEffect(() => {
    const id = pendingRef.current
    if (!id) return
    if (!visibleIds.includes(id)) return
    pendingRef.current = null
    selectRef.current(id)
  }, [visibleIds])

  return useCallback((id: string) => {
    pendingRef.current = id
  }, [])
}
